import { Controller } from "@hotwired/stimulus";
import { checkOnlineStatus } from "../../onlineStatus.js";
import {
  deleteSanitationRoute,
  getSanitationRouteBoundToDocument,
} from "../../db/sanitationRoutes.js";

import { getRouteDatapoints } from "../../db/routeDatapoints.js";

import { getRouteLocations } from "../../db/routeLocations.js";

import {
  uploadSanitationRouteWithFiles,
  uploadRouteMetadata,
  cleanupUploadedFiles,
} from "../../db/fileUpload.js";

import { getFileQueueByRoute } from "../../db/fileQueue.js";
import { DebugLogger } from "../../db/debugLogger.js";
import { db } from "../../db/index.js";

let uploadOperationInProgress = false;

export function totalTreesForUpload(sanitationRoute, routeDatapoints) {
  let totalTreesInRoute;
  if (sanitationRoute.total_trees_in_route === Infinity) {
    const treeDatapoints = routeDatapoints.filter(
      (dp) => dp.tree_number != null && dp.tree_number > 0,
    );
    totalTreesInRoute =
      treeDatapoints.length > 0
        ? Math.max(...treeDatapoints.map((dp) => dp.tree_number))
        : 0;
  } else {
    totalTreesInRoute = sanitationRoute.total_trees_in_route;
  }

  return Number.isFinite(totalTreesInRoute) && totalTreesInRoute >= 0
    ? Math.floor(totalTreesInRoute)
    : 0;
}

export function mapToServerShapeRouteDatapoints(routeDatapoints) {
  return routeDatapoints.map((routeDatapoint) => {
    return Object.assign(routeDatapoint.data.route_datapoint, {
      custom_field_values_attributes: (
        routeDatapoint.data.route_datapoint.custom_field_values_attributes || []
      ).map(({ file, ...rest }) => ({
        ...rest,
        value: rest.value ?? null,
      })),
      created_at: routeDatapoint.created_at,
      tree_number: routeDatapoint.tree_number,
    });
  });
}

// Connects to data-controller="sanitation-routes--upload-button"
export default class extends Controller {
  static targets = [
    "uploadButton",
    "loadingSpinner",
    "successMessage",
    "errorMessage",
    "progressContainer",
    "progressBar",
    "statusText",
    "retryButton",
  ];

  static values = {
    confirmUpload: Boolean,
  };

  connect() {
    this.lastUploadData = null; // Store last upload attempt for retry
    this.lastServerRouteId = null; // Store server route ID for retry (skip metadata re-upload)
    this.handleUploadClick = this.handleUploadClick.bind(this);
    this.element.addEventListener("click", this.handleUploadClick, {
      capture: true,
    });
  }

  disconnect() {
    this.element.removeEventListener("click", this.handleUploadClick, {
      capture: true,
    });
  }

  handleUploadClick(event) {
    const uploadButton = event.target.closest(
      "[data-sanitation-routes--upload-button-target~='uploadButton']",
    );

    if (!uploadButton || !this.element.contains(uploadButton)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.uploadRoute();
  }

  async getRouteData() {
    const routeIdElement = this.uploadButtonTarget.closest(
      "[data-sanitation-route-id]",
    );
    const explicitRouteId = routeIdElement
      ? Number(routeIdElement.dataset.sanitationRouteId)
      : null;

    const sanitationRoute = await getSanitationRouteBoundToDocument({
      explicitRouteId,
    });

    if (!sanitationRoute) {
      throw new Error("No bound sanitation route found");
    }

    const routeDatapoints = await getRouteDatapoints(sanitationRoute.id);
    const routeLocations = await getRouteLocations(sanitationRoute.id);

    return {
      sanitationRoute,
      routeDatapoints,
      routeLocations,
    };
  }

  async uploadRoute() {
    if (uploadOperationInProgress) return;

    uploadOperationInProgress = true;
    this.disableUploadButton();
    this.showLoadingSpinner();

    let sanitationRoute;
    let routeDatapoints;
    let routeLocations;
    let totalTreesInRoute;
    let hasFiles = false;

    try {
      this.prepareForUploadAttempt();

      // Local (IndexedDB) validation first — fast feedback before any network call
      ({ sanitationRoute, routeDatapoints, routeLocations } =
        await this.getRouteData());

      if (routeDatapoints.length === 0) {
        alert(
          "Esta ruta no tiene muestras. Por favor agrega muestras de datos antes de subir la ruta.",
        );
        this.hideLoadingSpinner();
        this.enableUploadButton();
        return;
      }

      if ((await checkOnlineStatus()) === false) {
        alert(
          "Estas sin conexión a internet. Intenta de nuevo uno vez que tengas conexión.",
        );
        this.hideLoadingSpinner();
        this.enableUploadButton();
        return;
      }

      if (this.confirmUploadValue) {
        if (
          !confirm(
            "¿Estás seguro que deseas subir la ruta de sanidad sin completarla?",
          )
        ) {
          this.hideLoadingSpinner();
          this.enableUploadButton();
          return;
        }
      } else {
        if (!confirm("¿Estás seguro que deseas subir la ruta de sanidad?")) {
          this.hideLoadingSpinner();
          this.enableUploadButton();
          return;
        }
      }

      totalTreesInRoute = totalTreesForUpload(sanitationRoute, routeDatapoints);

      // Ensure completed_at is set so the server idempotency check works on retry.
      // Without this, nil completed_at causes the server to generate a timestamp
      // that can't be matched on subsequent uploads, leading to duplicate routes.
      if (!sanitationRoute.completed_at) {
        sanitationRoute.completed_at = new Date().toISOString();
        await db.sanitationRoutes.update(sanitationRoute.id, {
          completed_at: sanitationRoute.completed_at,
        });
      }

      // Check if route has files to upload
      const fileQueue = await getFileQueueByRoute(sanitationRoute.id);
      hasFiles = fileQueue.length > 0;

      // Store upload data for potential retry
      this.lastUploadData = {
        sanitationRoute,
        routeDatapoints,
        routeLocations,
        totalTreesInRoute,
      };

      if (hasFiles) {
        // Use new split upload process for routes with files
        const result = await uploadSanitationRouteWithFiles(
          {
            sanitationRoute: sanitationRoute,
            routeDatapoints: mapToServerShapeRouteDatapoints(routeDatapoints),
            routeLocations: routeLocations || [],
            totalTreesInRoute: totalTreesInRoute,
          },
          (stage, progress, fileInfo) => {
            // Update UI based on upload progress
            this.updateUploadProgress(stage, progress, fileInfo);
          },
        );
        // Store server route ID so retry can skip metadata re-upload
        this.lastServerRouteId = result.serverRouteId;
      } else {
        // Metadata-only upload, batched for large routes (see fileUpload.js)
        this.lastServerRouteId = await uploadRouteMetadata(
          {
            sanitationRoute,
            routeDatapoints: mapToServerShapeRouteDatapoints(routeDatapoints),
            routeLocations: routeLocations || [],
            totalTreesInRoute,
          },
          (stage, progress) => this.updateUploadProgress(stage, progress),
        );
      }

      // Clean up local data after successful upload
      await deleteSanitationRoute(sanitationRoute.id);
      if (hasFiles) {
        await cleanupUploadedFiles(sanitationRoute.id);
      }

      this.showSuccessMessage();
      this.element.dispatchEvent(
        new CustomEvent("sanitation-route:uploaded", {
          bubbles: true,
          detail: { routeId: sanitationRoute.id },
        }),
      );
      this.hideLoadingSpinner();
      this.disableUploadButton();
    } catch (error) {
      DebugLogger.error("Route upload failed", {
        error: error.message,
        hasFiles,
        routeId: sanitationRoute?.id,
        datapointCount: routeDatapoints?.length || 0,
        totalTreesInRoute,
        userAgent: navigator.userAgent,
      });

      // Capture serverRouteId from partial success (metadata uploaded, files failed)
      // so retry can skip metadata re-upload
      if (error.serverRouteId) {
        this.lastServerRouteId = error.serverRouteId;
      }

      // Files are preserved in IndexedDB for retry — do NOT clean them up here.
      // cleanupOldFiles (7-day TTL) and cleanupOrphanedFiles (24h) handle abandoned files.

      this.showErrorMessage(error.message);
      this.hideLoadingSpinner();
      this.disableUploadButton();
    } finally {
      uploadOperationInProgress = false;
    }
  }

  mapToServerShapeRouteDatapoints(routeDatapoints) {
    return mapToServerShapeRouteDatapoints(routeDatapoints);
  }

  showLoadingSpinner() {
    this.loadingSpinnerTarget.classList.remove("hidden");
  }

  hideLoadingSpinner() {
    this.loadingSpinnerTarget.classList.add("hidden");
  }

  showSuccessMessage() {
    this.hideErrorMessage();
    this.hideRetryButton();
    this.successMessageTarget.classList.remove("hidden");
  }

  hideSuccessMessage() {
    this.successMessageTarget.classList.add("hidden");
  }

  showErrorMessage(specificError = null) {
    this.hideSuccessMessage();
    this.errorMessageTarget.classList.remove("hidden");

    // If we have a specific error, try to show more helpful message
    if (specificError && this.errorMessageTarget) {
      const errorElement =
        this.errorMessageTarget.querySelector("p") || this.errorMessageTarget;
      const baseMessage = errorElement.textContent;

      // Add specific error details for common issues
      if (specificError.includes("timeout")) {
        errorElement.textContent =
          "Subida cancelada por timeout. Revisa tu conexión e intenta de nuevo.";
      } else if (specificError.includes("Network")) {
        errorElement.textContent =
          "Error de conexión. Verifica tu internet e intenta de nuevo.";
      } else if (
        specificError.includes("quota") ||
        specificError.includes("storage")
      ) {
        errorElement.textContent =
          "Espacio de almacenamiento insuficiente. Libera espacio e intenta de nuevo.";
      } else if (
        specificError.includes("500") ||
        specificError.includes("502") ||
        specificError.includes("503")
      ) {
        errorElement.textContent =
          "Error del servidor. Intenta de nuevo en unos minutos.";
      } else {
        // Keep the original message for unknown errors
        errorElement.textContent = baseMessage;
      }
    }
  }

  hideErrorMessage() {
    this.errorMessageTarget.classList.add("hidden");
  }

  prepareForUploadAttempt() {
    this.hideSuccessMessage();
    this.hideErrorMessage();
    this.hideRetryButton();
    this.resetProgress();
  }

  disableUploadButton() {
    this.uploadButtonTarget.setAttribute("disabled", true);
  }

  enableUploadButton() {
    this.uploadButtonTarget.removeAttribute("disabled");
  }

  updateUploadProgress(stage, progress, fileInfo = null) {
    console.log(`Upload progress - Stage: ${stage}, Progress: ${progress}%`);

    // Show progress container when upload starts
    if (stage !== "idle") {
      this.showProgressContainer();
    }

    // Update progress bar
    this.updateProgressBar(progress);

    // Update status text based on stage
    this.updateStatusText(stage, progress, fileInfo);
    this.updateCardUploadBanner(stage, progress, fileInfo);

    // Update button text
    const buttonText =
      this.uploadButtonTarget.querySelector("span:not(.loading)");
    if (buttonText) {
      switch (stage) {
        case "metadata":
          buttonText.textContent = "Subiendo datos...";
          break;
        case "files":
          buttonText.textContent = "Subiendo archivos...";
          break;
        case "completed":
          buttonText.textContent = "Completado";
          this.hideProgressContainer();
          break;
        case "error":
          buttonText.textContent = "Error";
          this.showRetryButton();
          break;
        default:
          buttonText.textContent = "Subiendo...";
      }
    }
  }

  updateProgressBar(progress) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = `${progress}%`;
    }
  }

  updateStatusText(stage, progress, fileInfo) {
    if (!this.hasStatusTextTarget) return;

    const statusText = this.uploadStatusMessage(stage, progress, fileInfo);
    this.statusTextTarget.textContent = statusText;
  }

  updateCardUploadBanner(stage, progress, fileInfo) {
    let card;
    try {
      card = this.element.closest("li");
    } catch {
      return;
    }
    if (!card) return;

    const banner = card.querySelector(".js-route-upload-banner");
    if (!banner) return;

    if (stage === "idle" || stage === "completed" || stage === "error") {
      banner.classList.add("hidden");
      return;
    }

    banner.classList.remove("hidden");
    banner.classList.add("flex");

    const progressBar = banner.querySelector(".js-route-upload-progress-bar");
    if (progressBar) {
      progressBar.style.width = `${progress}%`;
    }

    const statusText = banner.querySelector(".js-route-upload-status-text");
    if (statusText) {
      statusText.textContent = this.uploadStatusMessage(
        stage,
        progress,
        fileInfo,
      );
    }
  }

  uploadStatusMessage(stage, progress, fileInfo) {
    switch (stage) {
      case "metadata":
        return `Enviando datos de la ruta... ${progress}%`;
      case "files":
        if (fileInfo) {
          return `Subiendo archivos: ${fileInfo.current}/${fileInfo.total} (${progress}%)`;
        }
        return `Subiendo archivos... ${progress}%`;
      case "completed":
        return "¡Subida completada exitosamente!";
      case "error":
        return "Error durante la subida. Puedes intentar de nuevo.";
      default:
        return "Preparando subida...";
    }
  }

  showProgressContainer() {
    if (this.hasProgressContainerTarget) {
      this.progressContainerTarget.classList.remove("hidden");
    }
  }

  hideProgressContainer() {
    if (this.hasProgressContainerTarget) {
      setTimeout(() => {
        this.progressContainerTarget.classList.add("hidden");
        this.resetProgress();
      }, 2000); // Hide after 2 seconds
    }
  }

  resetProgress() {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = "0%";
    }
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = "Preparando subida...";
    }
  }

  showRetryButton() {
    if (this.hasRetryButtonTarget) {
      this.retryButtonTarget.classList.remove("hidden");
    }
  }

  hideRetryButton() {
    if (this.hasRetryButtonTarget) {
      this.retryButtonTarget.classList.add("hidden");
    }
  }

  // Retry failed upload
  async retryUpload() {
    if (!this.lastUploadData) {
      console.error("No upload data available for retry");
      return;
    }

    console.log("Retrying upload...");

    this.prepareForUploadAttempt();

    // Show loading state
    this.showLoadingSpinner();
    this.disableUploadButton();

    const {
      sanitationRoute,
      routeDatapoints,
      routeLocations,
      totalTreesInRoute,
    } = this.lastUploadData;

    // Check if route has files to upload
    const fileQueue = await getFileQueueByRoute(sanitationRoute.id);
    const hasFiles = fileQueue.length > 0;

    try {
      if (hasFiles) {
        // Pass lastServerRouteId to skip metadata re-upload when metadata
        // already succeeded (e.g. only file uploads failed on first attempt)
        const result = await uploadSanitationRouteWithFiles(
          {
            sanitationRoute: sanitationRoute,
            routeDatapoints: mapToServerShapeRouteDatapoints(routeDatapoints),
            routeLocations: routeLocations || [],
            totalTreesInRoute: totalTreesInRoute,
          },
          (stage, progress, fileInfo) => {
            this.updateUploadProgress(stage, progress, fileInfo);
          },
          this.lastServerRouteId,
        );
        this.lastServerRouteId = result.serverRouteId;
      } else {
        this.lastServerRouteId = await uploadRouteMetadata(
          {
            sanitationRoute,
            routeDatapoints: mapToServerShapeRouteDatapoints(routeDatapoints),
            routeLocations: routeLocations || [],
            totalTreesInRoute,
          },
          (stage, progress) => this.updateUploadProgress(stage, progress),
        );
      }

      // Clean up local data after successful retry
      await deleteSanitationRoute(sanitationRoute.id);
      if (hasFiles) {
        await cleanupUploadedFiles(sanitationRoute.id);
      }

      this.showSuccessMessage();
    } catch (error) {
      console.error("Retry upload error:", error);

      // Capture serverRouteId for next retry attempt
      if (error.serverRouteId) {
        this.lastServerRouteId = error.serverRouteId;
      }

      // Files are preserved in IndexedDB for next retry attempt.

      this.showErrorMessage(error.message);
      this.showRetryButton(); // Show retry button again
    }

    this.hideLoadingSpinner();
    this.disableUploadButton();
  }
}
